import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Brain, Layers, Image as ImageIcon, Database } from 'lucide-react';
import { Badge } from '../../../../../components/ui/Badge';
import { IssueRow, MODULE_CONFIG } from '../shared';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
interface QuestionDetailPanelProps {
  row: UnifiedQuestionRow;
}
export function QuestionDetailPanel({
  row
}: QuestionDetailPanelProps) {
  const modConfig = MODULE_CONFIG[row.moduleCode] || {
    bgClass: 'bg-gray-500',
    textClass: 'text-gray-700'
  };
  return <div className="px-4 py-4 bg-gray-50 border-t border-gray-100" data-id="element-3522">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3523">
        {/* Left Column: Pipeline Issues */}
        <div className="space-y-4" data-id="element-3524">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5" data-id="element-3525">
            <AlertTriangle className="w-3.5 h-3.5" data-id="element-3526" /> Pipeline Issues
          </h4>

          {row.pipelineIssues.length === 0 ? <div className="p-4 bg-white rounded-lg border border-gray-200 text-center" data-id="element-3527">
              <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" data-id="element-3528" />
              <p className="text-sm text-gray-600" data-id="element-3529">
                Geen pipeline issues gevonden.
              </p>
            </div> : <div className="space-y-2" data-id="element-3530">
              {row.pipelineIssues.map((issue, idx) => <IssueRow key={idx} issue={issue} data-id="element-3531" />)}
            </div>}
        </div>

        {/* Right Column: Metadata & Quality */}
        <div className="space-y-6" data-id="element-3532">
          {/* Quality & Metadata */}
          <div data-id="element-3533">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3534">
              <Brain className="w-3.5 h-3.5" data-id="element-3535" /> Kwaliteit & Metadata
            </h4>

            <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4" data-id="element-3536">
              <div className="grid grid-cols-2 gap-4" data-id="element-3537">
                <div data-id="element-3538">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3539">Cognitief Niveau</p>
                  <p className="text-sm font-medium text-gray-900" data-id="element-3540">
                    Niveau {row.cognitiveLevel}
                  </p>
                </div>
                <div data-id="element-3541">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3542">Module & Tier</p>
                  <div className="flex items-center gap-1.5" data-id="element-3543">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${modConfig.bgClass} text-white`} data-id="element-3544">
                      {row.moduleCode}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-3545">
                      Tier {row.tier}
                    </span>
                  </div>
                </div>
                <div className="col-span-2" data-id="element-3546">
                  <p className="text-xs text-gray-500 mb-1" data-id="element-3547">Skill</p>
                  <p className="text-sm font-medium text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded inline-block" data-id="element-3548">
                    {row.skill}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Seed Status */}
          <div data-id="element-3549">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3550">
              <Database className="w-3.5 h-3.5" data-id="element-3551" /> Seed Status
            </h4>

            {row.seedWarnings.length === 0 ? <div className="p-3 bg-white rounded-lg border border-gray-200 flex items-center gap-2" data-id="element-3552">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-3553" />
                <span className="text-sm text-gray-600" data-id="element-3554">
                  In sync met taxonomy
                </span>
              </div> : <div className="space-y-2" data-id="element-3555">
                {row.seedWarnings.map((warn, i) => <div key={i} className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-800 font-mono" data-id="element-3556">
                    {warn}
                  </div>)}
              </div>}
          </div>

          {/* Visual Status */}
          <div data-id="element-3557">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-3" data-id="element-3558">
              <ImageIcon className="w-3.5 h-3.5" data-id="element-3559" /> Visuele Hulpmiddelen
            </h4>

            <div className="p-3 bg-white rounded-lg border border-gray-200" data-id="element-3560">
              {row.hasVisualProblems ? <div className="flex items-start gap-2 text-red-700" data-id="element-3561">
                  <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3562" />
                  <span className="text-sm" data-id="element-3563">
                    Er zijn problemen met de visuals (zie pipeline issues).
                  </span>
                </div> : <div className="flex items-start gap-2 text-emerald-700" data-id="element-3564">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-3565" />
                  <span className="text-sm" data-id="element-3566">
                    Visuals zijn geldig en correct geconfigureerd.
                  </span>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}